import React from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { useFormContext } from 'react-hook-form'
import { stringify as stringifyYaml } from 'yaml'
import { debounce } from 'lodash'
import { CodeBlock } from '@contrast-security-inc/design-system-surfaces'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { IconDownload } from '@contrast-security-inc/design-system-foundations'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import {
  WizardStepContext,
  WizardStepContextValueType
} from '../agent-wizard-steps'
import {
  populateCode,
  useDownloadCodeblock
} from '../common/download-codeblock'
import { OPERATING_SYSTEM, TYPING_DEBOUNCE_MS } from '../common/constants'
import {
  getCurrentLanguage,
  getOrgUuid,
  metadataDisplayToAgentLabel
} from '../common/utils'
import { useTeamserverUrl } from '../../../../teamserver-url/api/use-teamserver-url'
import { useSplitClient } from 'contrastBase/state'

export const codeApiYaml = `api:
  url: {agentUrl}
  api_key: {api_key}
  service_key: {service_key}
  user_name: {user_uid}`

const codeApiTokenLegacyEnv = `{envSetKeyword}CONTRAST__API__URL={agentUrl}
{envSetKeyword}CONTRAST__API__API_KEY={api_key}
{envSetKeyword}CONTRAST__API__SERVICE_KEY={service_key}
{envSetKeyword}CONTRAST__API__USER_NAME={user_uid}
`

export enum PreviewType {
  TOKEN,
  LEGACY,
  CONFIGURATION,
  PIPELINE
}

export const appendMetadataToYaml = (
  apiYaml: string,
  agentUid?: string,
  envSetKeyword?: string,
  previewType?: PreviewType,
  data?: any,
  metadata?: any
) => {
  const app = buildApp(data, metadata)

  const appText = buildAppText(
    isAppPopulated(app),
    app,
    agentUid,
    envSetKeyword,
    previewType
  )

  if (PreviewType.CONFIGURATION === previewType) {
    const object = {
      appText
    }

    return populateCode(apiYaml, object)
  }

  return `${apiYaml}
${appText}`
}

const isAppPopulated = (app) => {
  // ensure app is populated, we don't want an empty key
  // prettier-ignore
  const keysLen = Object.keys(app.application)
    .filter((key) => app.application[key]).length

  return keysLen === 0
}

export const buildApp = (appData?: any, appMetadata?: any) => {
  const app = {
    application: {
      name: undefined,
      group: undefined,
      tags: undefined,
      metadata: undefined
    }
  }

  if (appData?.name) {
    app.application.name = appData.name
  }

  if (appData?.appGroups?.length) {
    app.application.group = appData.appGroups.join(',')
  }

  if (appData?.appTags?.length) {
    app.application.tags = appData.appTags.join(',')
  }

  if (appMetadata?.length) {
    app.application.metadata = formatMetadataToRFC2253(appMetadata)
  }

  return app
}

const escapeRFC2253 = (value) => {
  // Step 1: Escape special characters according to RFC 2253: , + " \ < > ; = ( )
  const escapedValue = value.replace(/[,+"\\<>;=()]/g, '\\$&')

  // Step 2: Escape leading and trailing spaces using string methods
  const trimmedStart = escapedValue.startsWith(' ')
    ? '\\ ' + escapedValue.slice(1)
    : escapedValue
  const trimmedEnd = trimmedStart.endsWith(' ')
    ? trimmedStart.slice(0, -1) + '\\ '
    : trimmedStart

  return trimmedEnd
}

export const formatMetadataToRFC2253 = (metadataArray) => {
  // Transform metadata array to RFC 2253 compliant string
  const formattedEntries = metadataArray.map(({ agentLabel, value }) => {
    if (!agentLabel || !value) {
      throw new Error(`Invalid metadata entry: ${agentLabel}=${value}`)
    }

    // Trim the label and value to avoid unnecessary spaces
    const trimmedLabel = agentLabel.trim()
    const trimmedValue = value.trim()

    // Escape the value according to RFC 2253
    const escapedValue = escapeRFC2253(trimmedValue)

    // Return the formatted key="escaped_value" pair wrapped in double quotes
    return `${trimmedLabel}="${escapedValue}"`
  })

  // Join all key-value pairs with commas to form the final metadata string
  return formattedEntries.join(',')
}

export const buildAppText = (
  isAppPopulated: boolean,
  app,
  agentUid?: string,
  envSetKeyword?: string,
  previewType?: PreviewType
) => {
  const appNameKeyword =
    PreviewType.PIPELINE === previewType
      ? 'ContrastApplicationName: '
      : 'CONTRAST__APPLICATION__NAME='

  const appMetadataKeyword =
    PreviewType.PIPELINE === previewType
      ? 'ContrastApplicationMetadata: '
      : 'CONTRAST__APPLICATION__METADATA='

  const name = app?.application?.name
    ? `${envSetKeyword}${appNameKeyword}${app.application.name}`
    : ''
  const metadata = app?.application?.metadata
    ? `${envSetKeyword}${appMetadataKeyword}${app.application.metadata}`
    : ''

  const appText = `${name}
${metadata}`
  switch (previewType) {
    case PreviewType.TOKEN:
      return appText
    case PreviewType.LEGACY:
      return appText
    case PreviewType.PIPELINE:
      return appText
    case PreviewType.CONFIGURATION:
      const name = app?.application?.name
        ? 'application__name=' + app.application.name
        : ''
      let urlEncodedMetadata
      if (app?.application?.metadata) {
        const metadataJson = parseMetadata(app.application.metadata)
        urlEncodedMetadata = toQueryString(
          'application__metadata',
          metadataJson
        )
      }
      const metadata = !!urlEncodedMetadata ? urlEncodedMetadata : ''
      const agent = agentUid ? 'agentUid=' + agentUid : ''

      const properties = { agent, name, metadata }

      const queryString = Object.entries(properties)
        .filter(([_, value]) => value)
        .map(([_, value]) => `${value}`)
        .join('&')

      return queryString ? `?${queryString}` : ''

    default:
      return isAppPopulated ? '' : stringifyYaml(app)
  }
}

export const toQueryString = (
  prefix: string,
  obj: Record<string, string>
): string => {
  const str: string[] = []
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const encodedKey = encodeURIComponent(`${prefix}[${key}]`)
      const encodedValue = encodeURIComponent(obj[key])
      str.push(`${encodedKey}=${encodedValue}`)
    }
  }
  return str.join('&')
}

export const parseMetadata = (metadata) => {
  const regex = /([^,=]+)="((?:[^"\\]|\\.)*)"/g
  const result = {}
  let match

  while ((match = regex.exec(metadata))) {
    result[match[1].trim()] = match[2] // Trim key and assign value
  }

  return result
}

export const getMetadata = (formData) =>
  // prettier-ignore
  !!formData ? Object.keys(formData)
    .filter((it) => formData[it] && !['name', 'appGroups', 'appTags', 'agent'].includes(it))
    .map((it) => ({
      agentLabel: metadataDisplayToAgentLabel(it),
      value: formData[it]
    })) : []

type ApiPreviewYamlProps = {
  showDownloadYamlButton: boolean
  showRegisterApplicationButton?: boolean
  previewType?: PreviewType
}

const buildApiText = (
  multipleUsersEnabled: boolean,
  teamserverurl,
  apiCredentials,
  operatingSystem?: string,
  envSetKeyword?: string,
  previewType?: PreviewType
) => {
  const currentLanguage = getCurrentLanguage()

  switch (previewType) {
    case PreviewType.TOKEN:
      const token = apiCredentials.token
      return `${envSetKeyword}CONTRAST__API__TOKEN=${token}`
    case PreviewType.PIPELINE:
      return `${envSetKeyword}ContrastAPIToken: ${apiCredentials.token}`
    case PreviewType.LEGACY:
      return populateCode(codeApiTokenLegacyEnv, apiCredentials)
    case PreviewType.CONFIGURATION:
      return buildConfigurationCommand(
        multipleUsersEnabled,
        teamserverurl,
        apiCredentials,
        currentLanguage,
        operatingSystem
      )
    default:
      return populateCode(codeApiYaml, apiCredentials)
  }
}

export const buildConfigurationCommand = (
  multipleUsersEnabled: boolean,
  teamserverurl,
  apiCredentials,
  currentLanguage: string,
  operatingSystem?: string
) => {
  const linuxCommand = getLinuxCommand(
    multipleUsersEnabled,
    teamserverurl,
    apiCredentials,
    currentLanguage.replace(/_/g, '-')
  )

  const windowsCommand = getWindowsCommand(
    multipleUsersEnabled,
    teamserverurl,
    apiCredentials,
    currentLanguage.replace(/_/g, '-')
  )

  return operatingSystem === OPERATING_SYSTEM.WINDOWS
    ? windowsCommand
    : linuxCommand
}

const getAuth = (multipleUsersEnabled: boolean, apiCredentials) => {
  const userUid = multipleUsersEnabled
    ? apiCredentials.user_uid
    : apiCredentials.userUid
  const serviceKey = multipleUsersEnabled
    ? apiCredentials.service_key
    : apiCredentials.serviceKey
  return btoa(`${userUid}:${serviceKey}`)
}

const agentConfigUrl = '/agent/config{appText}'

const getWindowsCommand = (
  multipleUsersEnabled: boolean,
  teamserverUrl,
  apiCredentials,
  currentLanguage: string
) => {
  const windowsDirectory = `%ProgramData%\\\\contrast\\\\${currentLanguage.toLowerCase()}`

  return `mkdir ${windowsDirectory} && ^
curl --location --request GET "${teamserverUrl}${urlPath(
    multipleUsersEnabled
  )}" ^
-o ${windowsDirectory}\\\\contrast_security.yaml ^
-H "Authorization: ${getAuth(multipleUsersEnabled, apiCredentials)}" ^
-H "API-Key: ${apiCredentials.api_key}" ^
-H "Content-Type: application/x-yaml"  ^
-H "Accept: application/x-yaml"`
}

const getLinuxCommand = (
  multipleUsersEnabled: boolean,
  teamserverUrl,
  apiCredentials,
  currentLanguage: string
) => {
  const linuxDirectory = `/etc/contrast/${currentLanguage.toLowerCase()}`

  return `mkdir -p ${linuxDirectory} && \\
curl --location --request GET '${teamserverUrl}${urlPath(
    multipleUsersEnabled
  )}' \\
-o ${linuxDirectory}/contrast_security.yaml \\
-H 'Authorization: ${getAuth(multipleUsersEnabled, apiCredentials)}' \\
-H 'API-Key: ${apiCredentials.api_key}' \\
-H 'Content-Type: application/x-yaml'  \\
-H "Accept: application/x-yaml"`
}

const urlPath = (multipleUsersEnabled: boolean) => {
  return multipleUsersEnabled
    ? '/agents/v1.0/agents/configuration{appText}'
    : `/api/ng/organizations/${getOrgUuid()}${agentConfigUrl}`
}

const getEnvSetKeyword = (
  operatingSystem: string,
  previewType?: PreviewType
) => {
  if (previewType === PreviewType.PIPELINE) {
    return ''
  }

  return operatingSystem === OPERATING_SYSTEM.WINDOWS ? 'set ' : 'export '
}

export const adjustUrlForAgents = (
  url: string,
  multipleUsersEnabled: boolean,
  isEOP: boolean
): string => {
  if (!multipleUsersEnabled) {
    return url
  }

  const urlParts = url.split('/')
  const domainWithProtocol = urlParts.slice(0, 3).join('/') // Extracts "http://localhost" or "https://teamserver-staging.contsec.com"
  const remainingPath = urlParts.slice(3).join('/') // Extracts the part after the domain, e.g., "Contrast"

  if (isEOP) {
    return domainWithProtocol.replace(
      /(https?:\/\/)([^/]+)(.*)/,
      (_, protocol, _backendHostname, rest) => {
        return `${protocol}${location.hostname}${rest}/${remainingPath}`
      }
    )
  }

  const domainWithAgents = domainWithProtocol.replace(
    /(https?:\/\/)([^.]+)(.*)/,
    (_, protocol, hostname, rest) => {
      return rest
        ? // Handle complex domains (e.g., "teamserver-staging.contsec.com")
          `${protocol}${hostname}-agents${rest}`
        : // Handle simple domains (e.g., "localhost")
          `${protocol}${hostname}-agents`
    }
  )

  return `${domainWithAgents}/${remainingPath}`
}

export const ApiPreviewYaml: React.FC<ApiPreviewYamlProps> = ({
  showDownloadYamlButton,
  showRegisterApplicationButton,
  previewType
}) => {
  const ctxWizard = React.useContext(WizardStepContext)
  const operatingSystem = ctxWizard?.branches?.[OPERATING_SYSTEM.KEY]
  const envSetKeyword = getEnvSetKeyword(operatingSystem, previewType)
  const intl = useIntl()
  // const [metadata, setMetadata] = React.useState([])
  const [yamlCode, setYamlCode] = React.useState('')
  const { onClick: onClickDownloadYaml } = useDownloadCodeblock(
    { code: yamlCode },
    'contrast_security.yaml'
  )
  const ctx: WizardStepContextValueType = React.useContext(WizardStepContext)
  const { watch, getValues } = useFormContext()
  const { isSplitTreatmentOn } = useSplitClient()
  const isMultipleUsersEnabled = isSplitTreatmentOn(
    'PROD-3155_multiple_agent_users'
  )
  /*
  const onClickDownloadYamlOld = React.useCallback(async () => {
    const organizationUuid = getOrgUuid()
    const language = getCurrentLanguage()
    await downloadConfig({
      organizationID: organizationUuid,
      agentParam: language as Agent,
      body: { metadata },
      formatMessage: intl.formatMessage
    })
  }, [intl.formatMessage, metadata])
  */
  const apiCredentials = {
    ...ctx.apiCredentials,
    envSetKeyword
  }
  const { isEOP } = apiCredentials
  const { teamserverUrl: url } = useTeamserverUrl()

  const teamserverUrl = adjustUrlForAgents(
    url,
    !!isMultipleUsersEnabled,
    !!isEOP
  )
  const agentUid = isMultipleUsersEnabled ? undefined : apiCredentials.user_uid

  React.useEffect(() => {
    const initialFormData = getValues()
    const initialYaml = buildApiText(
      isMultipleUsersEnabled,
      teamserverUrl,
      apiCredentials,
      operatingSystem,
      envSetKeyword,
      previewType
    )
    const initialMetadata = getMetadata(initialFormData)
    const initialCode = appendMetadataToYaml(
      initialYaml,
      agentUid,
      envSetKeyword,
      previewType,
      initialFormData,
      initialMetadata
    )
    setYamlCode(initialCode)
  }, [getValues, apiCredentials, operatingSystem, envSetKeyword, previewType])

  React.useEffect(() => {
    const formSubscribe = watch(
      debounce((formData) => {
        const yaml = buildApiText(
          isMultipleUsersEnabled,
          teamserverUrl,
          apiCredentials,
          operatingSystem,
          envSetKeyword,
          previewType
        )
        const newMetadata = getMetadata(formData)

        setYamlCode(
          appendMetadataToYaml(
            yaml,
            agentUid,
            envSetKeyword,
            previewType,
            formData,
            newMetadata
          )
        )
        // setMetadata(newMetadata)
      }, TYPING_DEBOUNCE_MS)
    )

    return () => formSubscribe.unsubscribe()
  }, [watch, ctx, ctxWizard])

  return (
    <>
      <CodeBlock style={{ height: '100%' }} message={yamlCode} />
      <Stack flexDirection="row" justifyContent="flex-end">
        {showDownloadYamlButton ? (
          <Button
            className={css`
              width: 164px;
              align-self: end;
            `}
            onClick={onClickDownloadYaml}
            variant="secondary"
            Icon={IconDownload}
          >
            {intl.formatMessage({ id: 'title_downloadYAML' })}
          </Button>
        ) : (
          <></>
        )}
        {!!showRegisterApplicationButton ? (
          <Button type="submit">
            {intl.formatMessage({ id: 'title_registerApplication' })}
          </Button>
        ) : (
          <></>
        )}
      </Stack>
    </>
  )
}
